import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";

import { StyledAnchor } from "@/components/common/styles";
import { newTabProps } from "@/components/marketing/marketingLinks";
import { BODY } from "@/components/marketing/marketingType";
import {
  SECTION_PANEL,
  SECTION_PANEL_WASH,
  TEXT_BLOCK_PANEL,
} from "@/components/marketing/sectionPanel";
import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import {
  CheckLine,
  LandingButton,
  LandingImage,
  LandingSection,
  SectionHeading,
  unplacedLinks,
} from "../LandingPrimitives";
import { sectionPad } from "./landingRun";
import { AlternativeBox, WhyCard } from "./splitCards";
import { ChromeBox } from "./splitChrome";
import { ExtensionCard } from "./splitExtension";
import { RichParagraph } from "./splitText";

type Split = Laid<"split">;

/** The flat grounds, beside the wash. */
const GROUNDS = {
  surface: marketingPalette.surface,
  mint: marketingPalette.sectionMint,
  lilac: marketingPalette.sectionLilac,
} as const;

/**
 * `.content-left`, keyed by the art box it sits beside. A section whose art has
 * its own box does not halve the row: the art keeps its size, the copy keeps
 * this measure, and the slack falls between them.
 */
const COPY_COLUMN: Record<number, { width: string; gap: string }> = {
  400: { width: "558.89px", gap: "12px" },
  480: { width: "497.92px", gap: "16px" },
};

/**
 * The art column of `.main_header_box.is-alternative`, which is the grid's own
 * `1fr` rather than a square of its own: the registry records the width it
 * measures to at the wide step, and that number is what names the shape.
 */
const ALTERNATIVE_ART = 372;

/**
 * Copy on one side, a picture on the other. Five published shapes share the
 * kind, so each is drawn by its own component and this one picks between them.
 */
export default function SplitBlock({
  block,
  continues = false,
}: {
  block: Split;
  /** True when this block continues the section above rather than opening one. */
  continues?: boolean;
}) {
  if (block.level === 3 && block.ground === "surface") {
    return <WhyCard block={block} />;
  }
  if (block.imageBox === ALTERNATIVE_ART) {
    return <AlternativeBox block={block} />;
  }
  if (block.chromeBox) {
    return <ChromeBox block={block} />;
  }
  if (block.chip) {
    return <ExtensionCard block={block} />;
  }
  if (!block.image && block.headingAlign === "center") {
    return <TextPanel block={block} />;
  }
  return <PanelSplit block={block} continues={continues} />;
}

/** `.container_new.is-top_padding`: the break the copy-only panel opens on. */
const TEXT_PANEL_LEAD = {
  pt: "100px",
  [NOT_WIDE]: { pt: "64px" },
  [TINY]: { pt: "48px" },
} satisfies SxProps<Theme>;

/**
 * `.text-block_list`: four fifths of the box, the whole of it below the wide
 * step. Its lines centre as a column, so a one-line paragraph sits centred and
 * a longer one fills the measure, reading left as `.body_new` does.
 */
const TEXT_PANEL_COPY = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
  textAlign: "center",
  width: "80%",
  mx: "auto",
  [NOT_WIDE]: { width: 1 },
} satisfies SxProps<Theme>;

/**
 * Each paragraph's own links, in the order the copy sets them. Two phrases can
 * read the same ("Learn more") and go to different places, so a link is spent
 * on the first paragraph that sets its phrase.
 */
function linksByParagraph(
  paragraphs: ReadonlyArray<string>,
  links: Split["links"]
): Array<Split["links"]> {
  const left = [...(links ?? [])];
  return paragraphs.map((text) => {
    const own = left.filter(
      (link, index) =>
        text.includes(link.label) &&
        left.findIndex((other) => other.label === link.label) === index
    );
    for (const link of own) {
      left.splice(left.indexOf(link), 1);
    }
    return own;
  });
}

/**
 * The text-only panel: a centred heading over paragraphs of explanation, on
 * the page's ground. The paragraphs stay left-aligned, which reads better for
 * several lines than centring.
 */
function TextPanel({ block }: { block: Split }) {
  const paragraphs = block.paragraphs ?? [];
  const links = linksByParagraph(paragraphs, block.links);
  return (
    <LandingSection padding="none" sx={TEXT_PANEL_LEAD}>
      <Box
        sx={[
          { display: "flex" },
          SECTION_PANEL,
          TEXT_BLOCK_PANEL,
          panelGround(block),
        ]}
      >
        <Box
          sx={[
            TEXT_PANEL_COPY,
            block.purpleLinks
              ? { "& a": { color: marketingPalette.purple } }
              : null,
          ]}
        >
          <SectionHeading title={block.title} align="center" />
          {paragraphs.map((paragraph, index) => (
            <RichParagraph
              key={index}
              text={paragraph}
              links={links[index]}
              sx={{ ...BODY, textAlign: "left" }}
            />
          ))}
        </Box>
      </Box>
    </LandingSection>
  );
}

/** A flat ground's tint, where the page names one. */
function panelGround(block: Split) {
  return block.ground && block.ground !== "wash"
    ? { backgroundColor: GROUNDS[block.ground] }
    : null;
}

/**
 * `.main_header_wrap`: the box stands 112 under whatever precedes it, 64
 * below the wide step, a margin of the wrap's own rather than the section's.
 * Kept as padding here, since a margin would collapse out of a section that
 * has none of its own.
 */
const BOX_LEAD = {
  pt: "112px",
  [NOT_WIDE]: { pt: "64px" },
} satisfies SxProps<Theme>;

/** The row's shape: the boxed pair on its measures or the plain grid, on the ground the page names. */
function panelRowSx(block: Split, boxed: boolean) {
  return [
    boxed ? BOXED_ROW : gridRow(block.image ? "1fr 1fr" : "1fr"),
    boxed ? stackedRow(block) : null,
    block.ground ? SECTION_PANEL : null,
    block.ground === "wash" ? SECTION_PANEL_WASH : null,
    panelGround(block),
  ];
}

/** The art beside the copy, square at every width where the box names its size. */
function PanelArt({ block }: { block: Split }) {
  if (!block.image) {
    return null;
  }
  return (
    <LandingImage
      src={block.image}
      sx={[
        {
          width: block.imageBox ? `${block.imageBox}px` : 1,
          borderRadius: "16px",
          order: block.reversed ? 1 : 2,
        },
        // Square at every width: the art shrinks rather than
        // letterboxing inside a box that keeps its height. `minWidth`
        // lets it: a replaced element's automatic minimum is its own
        // width, so without it the row overflows instead of closing up.
        block.imageBox
          ? {
              aspectRatio: "1",
              minWidth: 0,
              maxWidth: "100%",
              objectFit: "contain",
              [MID]: { width: 1 },
            }
          : null,
      ]}
    />
  );
}

/** `.main_header_box`: the product pages' box, on the container's own measure. */
function PanelSplit({
  block,
  continues,
}: {
  block: Split;
  continues: boolean;
}) {
  const copy = block.imageBox ? COPY_COLUMN[block.imageBox] : undefined;
  const boxed = Boolean(block.image) && copy !== undefined;
  return (
    <LandingSection
      padding={continues ? "none" : sectionPad(block.pad)}
      sx={continues ? undefined : BOX_LEAD}
    >
      <Box sx={panelRowSx(block, boxed)}>
        <PanelCopy block={block} copy={copy} />
        <PanelArt block={block} />
      </Box>
    </LandingSection>
  );
}

/**
 * `alignItems` centres the art against the copy in the row, and both columns
 * once they stack.
 */
const BOXED_ROW = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "32px",
  [NOT_WIDE]: { flexDirection: "column" },
} satisfies SxProps<Theme>;

/**
 * Stacked, the published box keeps its row order, art first where the row
 * leads with it, unless it says otherwise, and closes to a gap authored per
 * instance: 16 on most boxes, 32 on the residential and static-residential ones.
 */
function stackedRow(block: Split) {
  return {
    [NOT_WIDE]: {
      gap: `${block.stackGap ?? 16}px`,
      flexDirection: block.stackArtFirst ? "column-reverse" : "column",
    },
  } satisfies SxProps<Theme>;
}

function gridRow(columns: string) {
  return {
    display: "grid",
    alignItems: "center",
    gap: "32px",
    gridTemplateColumns: columns,
    [NOT_WIDE]: { gridTemplateColumns: "1fr", gap: "24px" },
  };
}

/** The copy's lines: the lead, the list that belongs to it, then the rest. */
function PanelLines({
  block,
  lead,
  rest,
}: {
  block: Split;
  lead: string | undefined;
  rest: ReadonlyArray<string>;
}) {
  return (
    <>
      {/* The list belongs to the sentence introducing it, so it follows the
          first paragraph rather than all of them. */}
      {lead ? (
        <RichParagraph text={lead} links={block.links} sx={BODY} />
      ) : null}
      {block.bullets?.length ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {block.bullets.map((bullet, index) => (
            <CheckLine key={index} label={bullet} />
          ))}
        </Box>
      ) : null}
      {rest.map((paragraph, index) => (
        <RichParagraph
          key={index}
          text={paragraph}
          links={block.links}
          sx={BODY}
        />
      ))}
    </>
  );
}

/** `.button_new.is_fixed`: 200 wide where the plain control fills its row from the card step. */
const FIXED_CONTROL = {
  "& > a, & > button": { width: "200px", [TINY]: { width: 1 } },
} as const;

/** What the copy closes on: the links its lines set nowhere, and the controls. */
function PanelActions({
  block,
  loose,
}: {
  block: Split;
  loose: ReturnType<typeof unplacedLinks>;
}) {
  return (
    <>
      {loose.length ? <LooseLinks links={loose} /> : null}
      {block.ctas?.length ? (
        <Box
          sx={[
            { display: "flex", gap: "12px", flexWrap: "wrap" },
            block.fixedControl ? FIXED_CONTROL : null,
          ]}
        >
          {block.ctas.map((cta, index) => (
            <LandingButton
              key={index}
              cta={cta}
              variant={index === 0 ? "primary" : "secondary"}
            />
          ))}
        </Box>
      ) : null}
    </>
  );
}

function PanelCopy({
  block,
  copy,
}: {
  block: Split;
  copy: { width: string; gap: string } | undefined;
}) {
  const [lead, ...rest] = block.paragraphs ?? [];
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: copy?.gap ?? "16px",
        // A measure, not a cap: the published column shrinks against the art
        // rather than holding its width and pushing the picture out.
        width: copy?.width ?? "100%",
        order: block.reversed ? 2 : 1,
        // Stacked, the copy gives up its measure and takes the card's own.
        [MID]: { width: 1 },
      }}
    >
      <SectionHeading
        bodyTone={block.bodyTone}
        title={block.title}
        level={block.level === 3 ? "h3" : "h2"}
      />
      <PanelLines block={block} lead={lead} rest={rest} />
      <PanelActions
        block={block}
        loose={unplacedLinks(block.paragraphs, block.links)}
      />
    </Box>
  );
}

/** The links of a block that its copy sets nowhere, drawn as a row of their own. */
function LooseLinks({
  links,
}: {
  links: ReadonlyArray<{ label: string; href?: string; newTab?: boolean }>;
}) {
  return (
    <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
      {links.map((link, index) => (
        <StyledAnchor
          key={index}
          href={link.href}
          {...newTabProps(link)}
          sx={{
            color: marketingPalette.inkSubtle,
            borderBottom: `1px solid ${marketingPalette.outline}`,
          }}
        >
          {link.label}
        </StyledAnchor>
      ))}
    </Box>
  );
}
